import React, { useState } from 'react';
import { useAudit } from '../context/AuditContext';
import { PageFrame } from '../components/PageFrame';
import { SectionCard } from '../components/SectionCard';
import { NavigationControls } from '../components/NavigationControls';
import { TooltipWrapper, TOOLTIP_CONTENT } from '../components/TooltipWrapper';
import { DollarSign, Plus, Trash2, Calculator, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ROI_CATEGORIES, getProcessesForCategory, getCategoryOptions, getSubCategoryOptions, hasSubCategories, getProcessDifficulty } from '../data/roiCategories';
import { getSmartRoiSuggestions } from '../data/roiSuggestions';

export default function RoiPage() {
  const { auditData, addRoiItem, deleteRoiItem } = useAudit();
  const [newItem, setNewItem] = useState({
    category: '',
    sub_category: '',
    process: '',
    custom_process: '',
    hours_per_week: '',
    hourly_value: ''
  });

  const calculateAnnual = (hours, rate) => {
    const h = parseFloat(hours) || 0;
    const r = parseFloat(rate) || 0;
    return Math.round(h * r * 52);
  };

  const handleCategoryChange = (category) => {
    setNewItem({
      ...newItem,
      category,
      sub_category: '',
      process: '',
      custom_process: ''
    });
  };

  const handleSubCategoryChange = (subCategory) => {
    setNewItem({
      ...newItem,
      sub_category: subCategory,
      process: '',
      custom_process: ''
    });
  };

  const handleProcessChange = (process) => {
    setNewItem({
      ...newItem,
      process,
      custom_process: process === 'Custom...' ? newItem.custom_process : ''
    });
  };

  const handleAdd = () => {
    if (!newItem.category) {
      return;
    }
    
    const finalProcess = newItem.process === 'Custom...' ? newItem.custom_process : newItem.process;
    
    if (!finalProcess || !newItem.hours_per_week || !newItem.hourly_value) {
      return;
    }

    // Get difficulty from pre-defined values or default to 3 for custom
    // Use newItem.process (not finalProcess) to match against pre-defined list
    const difficulty = newItem.process === 'Custom...' 
      ? 3 
      : getProcessDifficulty(newItem.category, newItem.process, newItem.sub_category);

    const annual = calculateAnnual(newItem.hours_per_week, newItem.hourly_value);
    addRoiItem({
      category: newItem.category,
      sub_category: newItem.sub_category || null,
      process: finalProcess,
      hours_per_week: parseFloat(newItem.hours_per_week),
      hourly_value: parseFloat(newItem.hourly_value),
      difficulty,
      annual
    });
    
    setNewItem({
      category: '',
      sub_category: '',
      process: '',
      custom_process: '',
      hours_per_week: '',
      hourly_value: ''
    });
  };

  const totalAnnual = auditData.roi_items.reduce((sum, item) => sum + (item.annual || 0), 0);
  const smartSuggestions = getSmartRoiSuggestions(auditData);

  const addSuggestion = (suggestion) => {
    addRoiItem({
      category: suggestion.category,
      sub_category: suggestion.sub_category || null,
      process: suggestion.name,
      hours_per_week: suggestion.suggested_hours_per_week,
      hourly_value: suggestion.suggested_hourly_value,
      difficulty: suggestion.difficulty,
      annual: calculateAnnual(suggestion.suggested_hours_per_week, suggestion.suggested_hourly_value)
    });
  };

  // Get available processes based on category and subcategory
  const getAvailableProcesses = () => {
    if (!newItem.category) return [];
    if (hasSubCategories(newItem.category) && !newItem.sub_category) return [];
    return getProcessesForCategory(newItem.category, newItem.sub_category);
  };

  const showSubCategory = newItem.category && hasSubCategories(newItem.category);
  const showProcess = newItem.category && (!showSubCategory || newItem.sub_category);
  const showCustomInput = newItem.process === 'Custom...';

  // Group ROI items by category for display
  const groupedItems = auditData.roi_items.reduce((acc, item, index) => {
    const category = item.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push({ ...item, index });
    return acc;
  }, {});

  return (
    <PageFrame pageNumber={7} backgroundStyle="light">
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header bar */}
          <div className="bg-primary text-white rounded-lg p-6 text-center mb-6">
            <h1 className="text-3xl font-extrabold uppercase tracking-wide" data-testid="section-title">
              Quick ROI Estimator
            </h1>
            <p className="mt-2 text-white/90 max-w-3xl mx-auto">
              List the tasks that take your team the most time to perform, then estimate your AI ROI potential.
            </p>
          </div>

          {/* Smart starter suggestions */}
          {smartSuggestions.length > 0 && (
            <SectionCard title="Popular Starting Points" icon={Sparkles} className="mt-6 border-2 border-primary/20">
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Based on what you rated and wrote so far, these are common high-value tasks to consider. Add one, then adjust the hours and value if needed.
                </p>
                <div className="grid md:grid-cols-2 gap-3">
                  {smartSuggestions.map((suggestion) => (
                    <div key={`${suggestion.category}-${suggestion.sub_category || 'none'}-${suggestion.name}`} className="rounded-lg border border-muted bg-white p-4 space-y-3">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-primary">
                          {ROI_CATEGORIES[suggestion.category]?.label || suggestion.category}
                        </div>
                        <h3 className="font-bold text-foreground leading-snug">{suggestion.name}</h3>
                      </div>
                      <p className="text-xs text-muted-foreground">{suggestion.reason}</p>
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                        <span className="rounded-full bg-muted px-2 py-1">~{suggestion.suggested_hours_per_week} hrs/week</span>
                        <span className="rounded-full bg-muted px-2 py-1">${suggestion.suggested_hourly_value}/hr value</span>
                        <span className="rounded-full bg-muted px-2 py-1">Difficulty {suggestion.difficulty}/5</span>
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={() => addSuggestion(suggestion)} className="w-full">
                        <Plus className="h-4 w-4 mr-2" /> Add this task
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </SectionCard>
          )}

          {/* Calculator Card */}
          <SectionCard title="Add Task or Work Responsibility" icon={Calculator} className="mt-6">
            <div className="space-y-4">
              {/* Row 1: Category + Sub-category (if applicable) */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2 mb-1">
                    <Label htmlFor="category">Category *</Label>
                    <TooltipWrapper content={TOOLTIP_CONTENT.category} iconSize={14} />
                  </div>
                  <Select value={newItem.category} onValueChange={handleCategoryChange}>
                    <SelectTrigger data-testid="roi-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {getCategoryOptions().map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          <div>
                            <div className="font-semibold">{option.label}</div>
                            <div className="text-xs text-muted-foreground">{option.subtitle}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {showSubCategory && (
                  <div className="flex flex-col">
                    <Label htmlFor="sub-category" className="mb-1">Sub-category *</Label>
                    <Select value={newItem.sub_category} onValueChange={handleSubCategoryChange}>
                      <SelectTrigger data-testid="roi-subcategory-select">
                        <SelectValue placeholder="Select sub-category" />
                      </SelectTrigger>
                      <SelectContent>
                        {getSubCategoryOptions(newItem.category).map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Row 2: Task/Work Responsibility dropdown */}
              {showProcess && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Label htmlFor="process">Task/Work Responsibility *</Label>
                    <TooltipWrapper content={TOOLTIP_CONTENT.process} iconSize={14} />
                  </div>
                  <Select value={newItem.process} onValueChange={handleProcessChange}>
                    <SelectTrigger data-testid="roi-process-select">
                      <SelectValue placeholder="Select task/work responsibility" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableProcesses().map((process, idx) => {
                        const processName = typeof process === 'object' ? process.name : process;
                        return (
                          <SelectItem key={idx} value={processName}>
                            {processName}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Row 3: Custom task input (if Custom... selected) */}
              {showCustomInput && (
                <div>
                  <Label htmlFor="custom-process">Describe the Task/Work Responsibility *</Label>
                  <Input
                    id="custom-process"
                    value={newItem.custom_process}
                    onChange={(e) => setNewItem({ ...newItem, custom_process: e.target.value })}
                    placeholder="e.g., Creating quarterly board reports"
                    data-testid="roi-custom-process-input"
                  />
                </div>
              )}

              {/* Row 4: Hours + Hourly Value */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Label htmlFor="hours">Hours/Week Saved *</Label>
                    <TooltipWrapper content="Total time it takes all team members to complete task or perform function." iconSize={14} />
                  </div>
                  <Input
                    id="hours"
                    type="number"
                    value={newItem.hours_per_week}
                    onChange={(e) => setNewItem({ ...newItem, hours_per_week: e.target.value })}
                    placeholder="5"
                    data-testid="roi-hours-input"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Label htmlFor="rate">Hourly Value ($) *</Label>
                    <TooltipWrapper content={TOOLTIP_CONTENT.hourlyValue} iconSize={14} />
                  </div>
                  <Input
                    id="rate"
                    type="number"
                    value={newItem.hourly_value}
                    onChange={(e) => setNewItem({ ...newItem, hourly_value: e.target.value })}
                    placeholder="40"
                    data-testid="roi-rate-input"
                  />
                </div>
              </div>

              {/* Add Button */}
              <Button 
                onClick={handleAdd}
                className="w-full gap-2 mt-4"
                data-testid="roi-add-button"
                disabled={!newItem.category || !showProcess || !newItem.process || (showCustomInput && !newItem.custom_process) || !newItem.hours_per_week || !newItem.hourly_value}
              >
                <Plus className="h-4 w-4" />
                Add Task or Work Responsibility
              </Button>
            </div>
          </SectionCard>

          {/* ROI Table */}
          {auditData.roi_items.length > 0 && (
            <SectionCard title="Your Opportunities" icon={DollarSign} className="mt-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead>Task/Work Responsibility</TableHead>
                      <TableHead className="text-right">Hours/Week</TableHead>
                      <TableHead className="text-right">Hourly Value</TableHead>
                      <TableHead className="text-right">Annual Cost</TableHead>
                      <TableHead className="w-20"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.keys(groupedItems).map((category, catIdx) => {
                      // Use primary color with varying opacity for subtle differentiation
                      const opacities = ['bg-primary/5', 'bg-primary/8', 'bg-primary/11', 'bg-primary/14'];
                      const bgColor = opacities[catIdx % 4];
                      
                      return (
                        <React.Fragment key={category}>
                          {groupedItems[category].map((item, idx) => (
                            <TableRow key={item.index} className={bgColor}>
                              {idx === 0 && (
                                <TableCell 
                                  rowSpan={groupedItems[category].length}
                                  className="font-semibold text-primary"
                                >
                                  {ROI_CATEGORIES[category]?.label || 'Other'}
                                </TableCell>
                              )}
                              <TableCell className="font-medium">{item.process}</TableCell>
                              <TableCell className="text-right">{item.hours_per_week}</TableCell>
                              <TableCell className="text-right">${item.hourly_value}</TableCell>
                              <TableCell className="text-right font-bold text-primary">
                                ${item.annual.toLocaleString()}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteRoiItem(item.index)}
                                  data-testid={`roi-delete-${item.index}`}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      );
                    })}
                    <TableRow className="bg-accent/10 font-bold">
                      <TableCell colSpan={4} className="text-right">Total Annual Savings:</TableCell>
                      <TableCell className="text-right text-xl text-primary" data-testid="roi-total-annual">
                        ${totalAnnual.toLocaleString()}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </SectionCard>
          )}

          {/* Equation Box */}
          <div className="bg-accent/20 border-2 border-accent rounded-lg p-6 text-center mt-6">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Calculator className="h-6 w-6 text-secondary" />
              <span className="text-secondary font-bold text-lg">Hours × Rate × 52 = Annual Cost</span>
              <TooltipWrapper content={TOOLTIP_CONTENT.roiFormula} iconSize={16} />
            </div>
            <p className="mt-3 text-muted-foreground">
              Understanding the true cost of manual work helps identify where AI automation can deliver the greatest returns.
            </p>
          </div>

          {/* Navigation */}
          <div className="pt-4">
            <NavigationControls currentPage={7} />
          </div>
        </div>
      </div>
    </PageFrame>
  );
}
